import EMAILS from "@constants/emails";
import LINKS from "@constants/links";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Link,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { usePurchaseComplete } from "@hooks/usePurchaseComplete";
import { CATEGORY_MAP } from "@utils/purchaseUtils";

export default function PurchaseComplete() {
  const { purchaseData, hasUser, category, purchaseFlags } = usePurchaseComplete();

  if (!purchaseData) {
    return null;
  }

  const { source, user } = purchaseData;
  const isAuthenticated = source === "authenticated";

  return (
    <Card variant="outlined">
      <CardHeader
        title="Purchase Complete"
        avatar={<CheckCircleIcon color="primary" fontSize="large" />}
        slotProps={{ title: { variant: "h5" } }}
      />
      <CardContent>
        <Stack spacing={2}>
          {!isAuthenticated && <NewPurchaseSuccessMessage email={user.email} />}
          {isAuthenticated && hasUser && purchaseFlags && category && (
            <AuthenticatedSuccessMessage
              firstName={user.firstName}
              category={category}
              purchaseFlags={purchaseFlags}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

const NewPurchaseSuccessMessage = ({ email }: { email: string }) => {
  return (
    <>
      <section>
        <Typography variant="body1" gutterBottom>
          Thank you!
        </Typography>

        <Typography variant="body1" gutterBottom>
          We're excited that you've decided to join the Unyte Health provider community and look
          forward to working with you!
        </Typography>

        {email && (
          <Typography variant="body1" gutterBottom>
            The next important step is to create your MyUnyte account. An email with directions on
            how to create your MyUnyte account was sent to <b>{email}</b>. Please follow the
            directions in the email.
          </Typography>
        )}
      </section>

      <section>
        <Typography variant="h5" gutterBottom>
          Tips
        </Typography>
        <Divider />
        <List dense>
          <ListItem>
            <Typography variant="body1">
              1. Check your spam folder if you can't find the email.
            </Typography>
          </ListItem>
          <ListItem>
            <Typography variant="body1">
              2. Search your email inbox for an email from "{EMAILS.supportEmail}".
            </Typography>
          </ListItem>
        </List>
      </section>

      <section>
        <Typography variant="h5" gutterBottom>
          We're Here To Help
        </Typography>
        <Divider />
        <Typography variant="body1" gutterBottom>
          If you have any questions or need support, we are here to help.
        </Typography>
        <Typography variant="body1" gutterBottom>
          For answers to common questions, please visit{" "}
          <Link target="_blank" rel="noopener noreferrer" href={LINKS.support.homepageUrl}>
            {LINKS.support.domainName}
          </Link>
        </Typography>
        <Typography variant="body1" gutterBottom>
          We are also available by email at{" "}
          <Link href={`mailto:${EMAILS.supportEmail}`}>{EMAILS.supportEmail}</Link>
        </Typography>
      </section>
    </>
  );
};

interface AuthenticatedSuccessMessageProps {
  firstName: string;
  category: "ssp" | "focus" | "rrp" | "voicePro" | "mixed";
  purchaseFlags: {
    hasPhysicalProduct: boolean;
    hasCertifications: boolean;
    hasSubscriptions: boolean;
    hasSubscriptionsOnly: boolean;
    hasCertificationOnly: boolean;
    hasBundlePurchase: boolean;
    hasTrainingOnlyOrBundle: boolean;
  };
}

const AuthenticatedSuccessMessage = ({
  firstName,
  category,
  purchaseFlags,
}: AuthenticatedSuccessMessageProps) => {
  const { hasPhysicalProduct, hasTrainingOnlyOrBundle, hasSubscriptionsOnly } = purchaseFlags;

  // Handle iframe navigation for authenticated users
  const handleNavigation = (path: string) => {
    // Since authenticated users are in iframes, navigate parent window
    window.parent.postMessage(
      {
        type: "UNYTE_NAVIGATE",
        url: path,
        timestamp: Date.now(),
      },
      "*",
    );
  };

  return (
    <section>
      <Typography variant="body1" gutterBottom>
        {firstName.trim()}, thank you for your purchase!
      </Typography>

      {hasPhysicalProduct && (
        <Typography variant="body1" gutterBottom>
          Your order will be processed within 48 hours (2 business days) and submitted to the
          shipping courier. You will receive an email with tracking details once it's available.
        </Typography>
      )}

      {hasTrainingOnlyOrBundle && (
        <Typography variant="body1" gutterBottom>
          Your next step is to complete the{" "}
          {CATEGORY_MAP[category as keyof typeof CATEGORY_MAP].longTitle} Training, available
          through{" "}
          <Link
            component="button"
            onClick={() => handleNavigation("/certifications")}
            sx={{ textDecoration: "underline", cursor: "pointer" }}
          >
            Unyte Academy
          </Link>
          .
        </Typography>
      )}

      {hasSubscriptionsOnly && (
        <Typography variant="body1" gutterBottom>
          Your {CATEGORY_MAP[category as keyof typeof CATEGORY_MAP].longTitle} subscription is now
          active, and we are excited for you to begin delivering the{" "}
          {CATEGORY_MAP[category as keyof typeof CATEGORY_MAP].shortTitle} program.
        </Typography>
      )}
    </section>
  );
};
